import {
  ACTION,
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  Button,
  ButtonVariants,
  CustomerBalance,
  CustomerLedgerUpdateTypeEnum,
  Drawer,
  Flex,
  FormControlSizes,
  HStack,
  IconName,
  PanelHeader,
  Stack,
  Tab,
  TabList,
  TabPanels,
  TabSize,
  Tabs,
  tabLabelerEnumerated,
  useDrawer,
  useDynamicCallback,
  usePersistedTabs,
  usePortal,
  useTabs,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo, useState } from 'react';
import type { CustomerBalancesTabsProps } from './types';

import type { RowDoubleClickedEvent } from 'ag-grid-community';
import { useDetailsDrawer } from '../../../components/DetailsDrawer/useDetailsDrawer';
import { useFeatureFlag, useRoleAuth } from '../../../hooks';
import { useBlotterTableTabCloning } from '../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { CustomerBalancesBlotter } from './CustomerBalancesBlotter';
import {
  CUSTOMER_BALANCE_TRANSACTION_DRAWER_WIDTH,
  CustomerBalanceTransactionDrawer,
} from './CustomerBalanceTransactionDrawer';
import { DEFAULT_CUSTOMER_BALANCES_TABS } from './defaults';
import { LegacyBalanceTransactionDrawer } from './LegacyBalanceTransactionDrawer';

const CUSTOMER_BALANCES_BLOTTER_PREFIX = 'dealer/customer-balances';

const tabLabeler = tabLabelerEnumerated('Balances');

const TABS_EXCLUDED_IDS = compact(DEFAULT_CUSTOMER_BALANCES_TABS.map(tab => tab.id));
function getBlotterID(tabID: string | undefined) {
  return `${CUSTOMER_BALANCES_BLOTTER_PREFIX}/${tabID}`;
}

type CustomerBalancesProps = {
  showHeader?: boolean;
  hideBlotterActions?: boolean;
};

export const CustomerBalances = (props: CustomerBalancesProps) => {
  const { showHeader = true, hideBlotterActions = false } = props;
  const { isAuthorized } = useRoleAuth();

  const { requireCustomerBalancesCounterpartyFilter, enableNewCustomerBalanceTransactionDrawer } = useFeatureFlag();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(requireCustomerBalancesCounterpartyFilter);
  const handleSelect = useDynamicCallback(() => setInitialFiltersOpen(false));
  const handleAdd = useDynamicCallback(() => setInitialFiltersOpen(true));

  const [formDefaults, setFormDefaults] = useState<
    Partial<{
      customer?: string;
      customerAccount?: string;
      currency?: string;
      aliasCurrency?: string;
      transferType?: CustomerLedgerUpdateTypeEnum;
    }>
  >({});

  // reset form state when opening new
  const openNewBalanceTransactionDrawer = useDynamicCallback(
    (customerBalance?: CustomerBalance, transactionType?: CustomerLedgerUpdateTypeEnum) => {
      setFormDefaults({
        customer: customerBalance?.Counterparty,
        customerAccount: customerBalance?.MarketAccount,
        currency: customerBalance?.Currency,
        aliasCurrency: customerBalance?.Currency,
        transferType: transactionType ?? CustomerLedgerUpdateTypeEnum.Deposit,
      });
      balanceTransactionDrawer.open();
    }
  );

  const handleClickTransaction = useDynamicCallback(
    (customerBalance: CustomerBalance, transactionType?: CustomerLedgerUpdateTypeEnum) => {
      openNewBalanceTransactionDrawer(customerBalance, transactionType);
    }
  );

  const persistedTabs = usePersistedTabs<CustomerBalancesTabsProps>(CUSTOMER_BALANCES_BLOTTER_PREFIX, {
    defaultInitialItems: DEFAULT_CUSTOMER_BALANCES_TABS,
    excludedIDs: TABS_EXCLUDED_IDS,
    defaultInitialSelectedIndex: 0,
    onSelect: handleSelect,
  });

  const tabs = useTabs<CustomerBalancesTabsProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useDynamicCallback(() => {
    tabs.clone(tabs.selectedIndex, {
      closable: true,
      reorderable: true,
    });
  });

  const handleUpdateTab = useDynamicCallback((updatedTab: CustomerBalancesTabsProps) => {
    tabs.updateItem(updatedTab);
  });

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  const { openDetailsDrawer, closeDetailsDrawer, isOpen: isDrawerOpen } = useDetailsDrawer();

  const [lastSelectedRowID, setLastSelectedRowID] = useState<string | undefined>();
  const onRowDoubleClicked = useDynamicCallback(({ data, node }: RowDoubleClickedEvent<CustomerBalance>) => {
    // dont handle the event if there's no data, or we double clicked the Totals row
    if (!data || node.isRowPinned()) {
      return;
    }
    const id = data.rowID;
    // If clicking the same row, close the drawer
    if (lastSelectedRowID === id && isDrawerOpen) {
      setLastSelectedRowID(undefined);
      closeDetailsDrawer();
      return;
    }

    // If clicking a different row, open the drawer
    openDetailsDrawer({ entity: data, onClickTransaction: handleClickTransaction });
    setLastSelectedRowID(id);
  });

  const balanceTransactionDrawer = useDrawer({
    position: 'relative',
    width: CUSTOMER_BALANCE_TRANSACTION_DRAWER_WIDTH,
  });

  // Create CustomerBalance entity from form defaults
  const customerBalanceEntity = useMemo(() => {
    if (!formDefaults.customer || !formDefaults.customerAccount || !formDefaults.currency) {
      return undefined;
    }
    return new CustomerBalance({
      Counterparty: formDefaults.customer,
      MarketAccount: formDefaults.customerAccount,
      Currency: formDefaults.currency,
    } as CustomerBalance);
  }, [formDefaults.customer, formDefaults.customerAccount, formDefaults.currency]);

  return (
    <Flex h="100%" w="100%" gap="spacing2" overflow="hidden">
      <Stack flex="1">
        {showHeader && (
          <PanelHeader>
            <h2>Customer Balances</h2>
            <Button
              startIcon={IconName.ArrowLeftRight}
              onClick={() => openNewBalanceTransactionDrawer()}
              variant={ButtonVariants.Positive}
              disabled={!isAuthorized(ACTION.DEALER_TRADING)}
              size={FormControlSizes.Small}
              data-testid="customer-balances-new-balance-transaction-button"
            >
              New Balance Transaction
            </Button>
          </PanelHeader>
        )}

        <Stack overflow="hidden" flex="1" background="backgroundContent">
          <Tabs h="100%" flex="0 0 auto" {...tabs} size={TabSize.Large}>
            <TabList
              isBordered
              flex="0 0 auto"
              rightItems={
                <HStack gap="spacing12">
                  <Box ref={filtersContainerRef} />
                </HStack>
              }
            >
              {tabs.items.map((tab, index) => (
                <Tab key={index} {...tab} />
              ))}
            </TabList>
            <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
              {tabs.items.map(tab => (
                <AccordionGroup key={tab.id}>
                  <CustomerBalancesBlotter
                    tab={tab}
                    blotterID={`${CUSTOMER_BALANCES_BLOTTER_PREFIX}/${tab.id}`}
                    hideBlotterActions={hideBlotterActions}
                    initialFiltersOpen={initialFiltersOpen}
                    onCloneTab={handleCloneTab}
                    onTransactionPrime={openNewBalanceTransactionDrawer}
                    onUpdateTab={handleUpdateTab}
                    onRowDoubleClicked={onRowDoubleClicked}
                  />
                </AccordionGroup>
              ))}
            </TabPanels>
          </Tabs>
        </Stack>
      </Stack>
      <Drawer {...balanceTransactionDrawer}>
        {balanceTransactionDrawer.isOpen &&
          (enableNewCustomerBalanceTransactionDrawer ? (
            <CustomerBalanceTransactionDrawer
              onClose={() => balanceTransactionDrawer.close()}
              formDefaults={formDefaults}
            />
          ) : (
            <LegacyBalanceTransactionDrawer
              isOpen={balanceTransactionDrawer.isOpen}
              onClose={() => balanceTransactionDrawer.close()}
              entity={customerBalanceEntity}
              transactionType={formDefaults.transferType}
            />
          ))}
      </Drawer>
    </Flex>
  );
};
